import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers';
import { bindActionCreators } from 'redux';
import {
  disableSuspension,
  ENTITY_TYPE,
  getActorRoleString,
  getEntityTypeFromSuspensionType,
  isRoleTitan,
  toTitleCase,
  unwrapSuspensionReason,
} from './utils';
import {
  removeCustomerSuspensions,
  removeSuspensions,
  setEntitySuspension,
} from '../actions/orderActions';
import { ReactComponent as Success } from '../assets/suspend-success-tick.svg';
import { ReactComponent as CloseGrey } from '../assets/close-grey.svg';
import { ReactComponent as Alert } from '../assets/action_history_alert.svg';
import { suspensionListInfoType } from '../model/suspensionList';
import {
  CustomerSuspensionParams,
  CustomerSuspensionSource,
  UnsuspendEntityPayload,
} from '../model/customerSuspensionDetails';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from 'react-intl';
import {
  authTokenSelector,
  userRoleSelector,
} from '../selectors/userSelectors';
import {
  customerSuspensionsSelector,
  entitySuspensionsSelector,
} from '../selectors/suspensionSelectors';
import {
  bundleDetailsSelector,
  customerDetailsSelector,
  orderDetailsSelector,
} from '../selectors/bundleSelectors';
import { getLatestOrderOfOrderType } from '../helpers/product.helpers';

export interface UnsuspensionDetailsProps {
  suspensionListInfo: suspensionListInfoType;
  isUnsuspensionInProgress: boolean;
  isUnsuspensionSuccess: boolean;
  unsuspensionError: string;
  customerSuspensionOrderInfo: CustomerSuspensionParams;
  customerIdRegionDetails: any;
}

export interface ParentComponentProps {
  suspensionType: ENTITY_TYPE;
  orderType: string;
  accountId: Number;
  closeModal: (...args: any[]) => void;
  email: string | null;
  source: CustomerSuspensionSource | null;
}

type Props = UnsuspensionDetailsProps &
  ParentComponentProps &
  WrappedComponentProps;

const Unsuspension: React.FC<Props> = (props) => {
  const [note, setNote] = useState('');
  const [headerBoxChecked, setHeaderBoxChecked] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const token = useSelector(authTokenSelector);
  const userRole = useSelector(userRoleSelector);
  const { domainName } = useSelector(bundleDetailsSelector);
  const orders = useSelector(orderDetailsSelector);
  const entitySuspensions = useSelector(entitySuspensionsSelector);
  const customerDetails = useSelector(customerDetailsSelector);
  const isCustomerSuspended = useSelector(customerSuspensionsSelector)?.length;

  const dispatch = useDispatch();

  // Effect to handle unsuspension success (similar to componentDidUpdate)
  useEffect(() => {
    if (props.isUnsuspensionSuccess) {
      timeoutRef.current = setTimeout(() => {
        handleCloseClick();
      }, 1000);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [props.isUnsuspensionSuccess]);

  const handleNoteChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNote(event.target.value);
    },
    []
  );

  const getSuspensionDetailsList = useCallback(() => {
    if (props.suspensionType === ENTITY_TYPE.ACCOUNT) {
      return (
        entitySuspensions[
          ENTITY_TYPE.ACCOUNT.toUpperCase()
        ]?.suspensions.filter(
          (suspension: { entityId: Number }) =>
            suspension.entityId === props.accountId
        ) || []
      );
    }

    if (props.suspensionType === ENTITY_TYPE.ORDER) {
      return (
        entitySuspensions[ENTITY_TYPE.ORDER.toUpperCase()]?.suspensions.filter(
          (suspension: { entityId: Number }) =>
            suspension.entityId ===
            getLatestOrderOfOrderType(orders, props.orderType).id
        ) || []
      );
    }

    return (
      entitySuspensions[props.suspensionType.toUpperCase()]?.suspensions || []
    );
  }, [entitySuspensions, props.suspensionType]);

  const getCustomerOrders = useCallback(() => {
    return props.customerSuspensionOrderInfo.orderInfo.orders;
  }, [props.customerSuspensionOrderInfo]);

  const handleCloseClick = useCallback(
    (shouldRefetchData: boolean = true) => {
      props.closeModal(suspensionType, shouldRefetchData);
    },
    [props]
  );

  const handleSubmit = useCallback(() => {
    if (props.isUnsuspensionSuccess) {
      handleCloseClick();
      return;
    }

    // Build an array of suspension types from the checked items
    const suspensionTypes = getSuspensionDetailsList()
      .filter((item: { isChecked: boolean }) => item.isChecked)
      .map((item: { suspensionType: any }) => item.suspensionType);

    // Determine entityId and entityType based on the suspensionType
    let entityId;
    let entityType;

    switch (props.suspensionType) {
      case ENTITY_TYPE.CUSTOMER:
        entityId = customerDetails.id;
        entityType = ENTITY_TYPE.CUSTOMER.toUpperCase();
        break;
      case ENTITY_TYPE.ORDER:
        entityId = getLatestOrderOfOrderType(orders, props.orderType).id;
        entityType = ENTITY_TYPE.ORDER.toUpperCase();
        break;
      case ENTITY_TYPE.ACCOUNT:
        entityId = props.accountId;
        entityType = ENTITY_TYPE.ACCOUNT.toUpperCase();
        break;
      default:
        return;
    }

    const payload: UnsuspendEntityPayload = {
      entityId,
      entityType,
      suspensionTypes,
      note,
    };

    dispatch(setEntitySuspension(payload, 'unsuspend', token));
  }, [props, getSuspensionDetailsList, note, handleCloseClick]);

  const toggleAllBoxes = useCallback(() => {
    const newValue = !headerBoxChecked;
    setHeaderBoxChecked(newValue);
    const suspensionList = getSuspensionDetailsList();
    if (suspensionList) {
      suspensionList.forEach((item: any) => {
        if (!disableSuspension(userRole, item.role)) {
          item.isChecked = newValue;
        }
      });
    }
  }, [headerBoxChecked, getSuspensionDetailsList, userRole]);

  const toggleSelected = useCallback(
    (item: any) => {
      item.isChecked = !item.isChecked;
      setHeaderBoxChecked(
        getSuspensionDetailsList().every((i: any) => i.isChecked)
      );
    },
    [getSuspensionDetailsList]
  );

  const getAlertDivForUnsuspension = useCallback(() => {
    return (
      <div className="alert-container-unsuspension">
        <div className="alert-container-icon">
          <Alert />
        </div>
        <span className="alert-container-text">
          <FormattedMessage
            id="action-blocked-customer-suspended"
            values={{
              entity: getEntityTypeFromSuspensionType(props.suspensionType),
            }}
          />
        </span>
      </div>
    );
  }, [props.suspensionType]);

  const isUnsuspensionBlocked = useCallback(() => {
    return isCustomerSuspended && props.suspensionType !== ENTITY_TYPE.CUSTOMER;
  }, [isCustomerSuspended, props.suspensionType]);

  const createUnsuspensionText = useCallback(() => {
    switch (props.suspensionType) {
      case ENTITY_TYPE.ACCOUNT:
        return toTitleCase(
          props.intl.formatMessage({ id: 'unsuspend-account' })
        );
      case ENTITY_TYPE.ORDER:
        return toTitleCase(props.intl.formatMessage({ id: 'unsuspend-order' }));
      case ENTITY_TYPE.CUSTOMER:
        return toTitleCase(
          props.intl.formatMessage({ id: 'unsuspend-customer' })
        );
      default:
        return '';
    }
  }, [props.suspensionType, props.intl]);

  // Render variables and computed values
  const suspensionType = props.suspensionType;
  const modalSubtitle =
    suspensionType === ENTITY_TYPE.ORDER ? `@${domainName}` : props.email;
  const suspensionList = getSuspensionDetailsList();
  const disableUnsuspendButton =
    !suspensionList.some((item: any) => item.isChecked) ||
    props.isUnsuspensionInProgress ||
    isUnsuspensionBlocked();
  const unsuspensionNotePlaceholder = props.intl.formatMessage({
    id: 'unsuspension-note-placeholder',
  });
  let unsuspensionFooterNote = '';
  if (
    !(
      suspensionList.every((item: any) => item.isChecked) ||
      !suspensionList.some((item: any) => item.isChecked)
    )
  ) {
    unsuspensionFooterNote = props.intl.formatMessage(
      { id: 'unsuspension-footer-note-other-suspension-present' },
      { entity: getEntityTypeFromSuspensionType(props.suspensionType) }
    );
  }
  let unsuspendButtonText: string | ReactElement = props.intl.formatMessage({
    id: 'unsuspend',
  });
  if (props.isUnsuspensionInProgress) {
    unsuspendButtonText = (
      <div className="modal-button-div">
        <div className="modal-spinner" />
        <FormattedMessage id="unsuspending" />
      </div>
    );
  } else if (props.isUnsuspensionSuccess) {
    unsuspendButtonText = (
      <div className="modal-button-div">
        <Success className="modal-button-success-icon" />
        <FormattedMessage id="unsuspended" />
      </div>
    );
  }
  const areAllSuspensionsBlocked = suspensionList.every((item: any) =>
    disableSuspension(userRole, item.role)
  );
  const visibilityStyle: React.CSSProperties = {
    visibility: areAllSuspensionsBlocked ? 'hidden' : 'visible',
  };

  const tableHeaders = (
    <>
      <input
        type="checkbox"
        checked={headerBoxChecked}
        onClick={toggleAllBoxes}
        className="unsuspension-checkbox-header"
        style={visibilityStyle}
      />
      <th className="unsuspension-table-header-user">
        <FormattedMessage id="user" />
      </th>
      <th className="unsuspension-table-header-reason">
        <FormattedMessage id="reason" />
      </th>
    </>
  );

  const allSuspensions = suspensionList.map((item: any, index: number) => {
    const disableRow = disableSuspension(userRole, item.role);
    const disabledNote = props.intl.formatMessage({
      id: 'contact-titan-support-to-unsuspend',
    });
    const suspensionRoleString = getActorRoleString(item.role, userRole);
    const suspensionTypeString = props.intl.formatMessage({
      id: unwrapSuspensionReason(item.suspensionType),
    });
    const noteText = item.notes.filter((n: string) => n).join('\n--\n');
    const isTitanUser = isRoleTitan(userRole);
    return (
      <tr
        key={index}
        className={
          disableRow
            ? 'unsuspension-table-data-row-disabled'
            : 'unsuspension-table-data-row-enabled'
        }
      >
        <td className="unsuspension-checkbox">
          <input
            type="checkbox"
            checked={item.isChecked}
            onClick={() => toggleSelected(item)}
            className={disableRow ? 'checkbox-hidden' : 'checkbox-shown'}
          />
        </td>
        <td>
          <div className="unsuspension-table-cell unsuspension-table-cell-email">
            <text className="unsuspension-table-data-heading">
              {suspensionRoleString}
            </text>
            <text className="unsuspension-table-data-subheading">
              {disableRow ? '' : item.userEmail}
            </text>
          </div>
        </td>
        <td>
          <div className="unsuspension-table-cell">
            <text className="unsuspension-table-data-heading">
              {suspensionTypeString}
            </text>
            {isTitanUser && (
              <div className="unsuspension-table-data-subheading">
                {disableRow ? disabledNote : noteText}
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  });

  return (
    <div>
      <CloseGrey
        className="alert-modal-close-button"
        onClick={() => handleCloseClick(false)}
      />
      <div className="unsuspension-container">
        <div className="suspension-heading">{createUnsuspensionText()}</div>
        <div className="suspension-subheading">{modalSubtitle}</div>
        {props.suspensionType === ENTITY_TYPE.CUSTOMER
          ? null
          : // <div className="customer-suspension-order-summary-unsuspension">
            //   <div className="customer-suspension-order-summary-heading">
            //     <FormattedMessage id="orders-header" />
            //   </div>
            //   <div className="customer-suspension-order-summary-value">
            //     {getCustomerOrders().length}
            //   </div>
            //   <div className="customer-suspension-order-summary-heading">
            //     <FormattedMessage id="active-accounts-header" />
            //   </div>
            //   <div className="customer-suspension-order-summary-value">
            //     {getActiveAccountsForCustomerOrders(getCustomerOrders())}
            //   </div>
            //   <div className="customer-suspension-order-summary-heading">
            //     <FormattedMessage id="paid-accounts-header" />
            //   </div>
            //   <div className="customer-suspension-order-summary-value">
            //     {getPaidAccountsForCustomerOrders(getCustomerOrders())}
            //   </div>
            // </div>
            null}
        <div className="unsuspension-list-heading">
          <FormattedMessage id="suspensions-to-undo" />
        </div>
        {isUnsuspensionBlocked() ? getAlertDivForUnsuspension() : ''}
        <div className="unsuspension-table">
          <table>
            <tr className="unsuspension-table-header-row">{tableHeaders}</tr>
            {allSuspensions}
          </table>
        </div>
        <textarea
          className="unsuspension-note"
          placeholder={unsuspensionNotePlaceholder}
          onChange={handleNoteChange}
          value={note}
        />
        <div className="suspension-button-container">
          <button
            className="button button-primary suspension-button"
            disabled={disableUnsuspendButton}
            onClick={handleSubmit}
          >
            {unsuspendButtonText}
          </button>
          <button
            className="button suspension-button modal-cancel-button"
            onClick={() => handleCloseClick(false)}
          >
            <FormattedMessage id="cancel" />
          </button>
        </div>
        <div className="unsuspension-footer-note">{unsuspensionFooterNote}</div>
        {props.unsuspensionError ? (
          <div className="suspension-error">{props.unsuspensionError}</div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

export default connect(
  (state: RootState) => ({
    suspensionListInfo: (state.orders as any).suspensionListInfo,
    isUnsuspensionInProgress: (state.orders as any).isUnsuspensionInProgress,
    isUnsuspensionSuccess: (state.orders as any).isUnsuspensionSuccess,
    unsuspensionError: (state.orders as any).unsuspensionError,
    customerSuspensionOrderInfo: (state.orders as any)
      .customerSuspensionOrderInfo,
    customerIdRegionDetails: (state.orders as any).customerIdRegionDetails,
  }),
  (dispatch: any) =>
    bindActionCreators(
      { removeSuspensions, removeCustomerSuspensions },
      dispatch
    )
)(injectIntl(Unsuspension));
